import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  Input,
  Button,
  Modal,
  Space,
  InputNumber,
  message,
  Typography,Popconfirm,
  Tooltip,
} from 'antd';
import { SearchOutlined, SwapOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import { AuthContext } from '../../context/AuthContext';

const { Title } = Typography;

const TransferSpareParts = () => {
  const { user } = useContext(AuthContext);
  const userWareId = user?.warehouseId;

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [items, setItems] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  // Fetch requests where current warehouse is source
  const fetchRequests = async () => {
    try {
      const res = await api.get(`/api/spare-requests?sourceWarehouseId=${userWareId}`);
      console.log(res.data)
      setRequests(res.data);
      setFilteredRequests(res.data);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to fetch requests');
    }
  };

  useEffect(() => {
    if (userWareId) fetchRequests();
  }, [userWareId]);

  // Filter requests by salesOrderNumber (for search or barcode)
  useEffect(() => {
    const filtered = requests.filter(r =>
      r.salesOrderNumber.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredRequests(filtered);
  }, [searchText, requests]);

  // Fetch existing stock for each item when modal opens
  const fetchExistingQty = async (sparepartId) => {
    try {
      const res = await api.get(`/api/inventories/check`, {
        params: { warehouseId: userWareId, sparepartId },
      });
      return res.data?.quantity || 0;
    } catch (err) {
      console.error('Error fetching existing qty', err);
      return 0;
    }
  };

  // Open modal and load items with existingQty
  const openModal = async (request) => {
    setSelectedRequest(request);

    const itemsWithQty = await Promise.all(
      request.items.map(async (i) => {
        const existingQty = await fetchExistingQty(i.sparepartId);
        return {
          ...i,
          approvedQty: i.requestedQty,
          existingQty,
          key: i.id,
        };
      })
    );
    console.log(itemsWithQty);
    setItems(itemsWithQty);
    setModalVisible(true);
  };

  // Handle quantity change
  const handleQtyChange = (index, value) => {
    const newItems = [...items];
    newItems[index].approvedQty = value;
    setItems(newItems);
  };

  // Remove an item from transfer
  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // Copy sales order number
  const handleCopyOrderNumber = (salesOrderNumber) => {
    navigator.clipboard.writeText(salesOrderNumber);
    messageApi.success(`Order number ${salesOrderNumber} copied!`);
  };

  // Submit transfer
const handleTransferSubmit = async () => {
  if (!items.length) {
    messageApi.error('No items to transfer');
    return;
  }

  // Check for insufficient stock
   const insufficientItems = items.filter(i => i.approvedQty > i.existingQty);
  if (insufficientItems.length > 0) {
    insufficientItems.forEach(i => {
      messageApi.error(`Not enough quantity for ${i.partName} (Existing: ${i.existingQty}, Approved: ${i.approvedQty})`);
    });
    return; // Stop submission if any item is insufficient
  }

  try {
    // Prepare payload only for items in the modal
    const payload = items.map(i => ({
      sparepartId: i.sparepartId,
      approvedQty: i.approvedQty,
      sourceWarehouseId: userWareId,
      status: 'sent', // update status here
    }));

    // Call API to update approvedQty and status
    await api.patch(
      `/api/spare-requests/${selectedRequest.salesOrderNumber}/send`,
      payload
    );

    messageApi.success('Spare parts updated and marked as sent!');

    // Print delivery note with the actual approvedQty and current list
    printDeliveryNote({
      warehouseName: user.warehouseName,
      salesOrderNumber: selectedRequest.salesOrderNumber,
      items, // use current items state
    });

    setModalVisible(false);
    fetchRequests();
  } catch (err) {
    const errorMessage = err?.response?.data?.message || 'Failed to update spare parts';
    messageApi.error(errorMessage);
    console.error(err);
  }
};
  const handleDeleteRequest = async (salesOrderNumber) => {
        try {
          await api.delete(`/api/spare-requests/order/${salesOrderNumber}`);
          messageApi.success('Spare part request deleted successfully!');
          fetchRequests(); // Refresh the list
        } catch (err) {
          const errorMessage = err?.response?.data?.message || 'Failed to delete spare part request';
          messageApi.error(errorMessage);
          console.error(err);
        }
      };
    




  // Printable delivery note
  const printDeliveryNote = ({ warehouseName, salesOrderNumber, items }) => {
    const today = new Date().toLocaleDateString();
    const html = `
      <html>
        <head>
          <title>Delivery Note</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { font-family: Arial; padding: 10px; font-size: 12px; }
            h2, h3 { text-align: center; margin: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #000; padding: 4px; text-align: left; }
            th { background-color: #f0f0f0; }
            .footer { margin-top: 10px; display: flex; justify-content: space-between; }
            .footer div { text-align: center; width: 30%; }
            .barcode { text-align: center; margin-top: 10px; }
          </style>
        </head>
        <body>
          <h2>JS CONSTRUCTION MACHINERY SPARE PARTS</h2>
          <h3>Delivery Note</h3>
          <p><strong>Date:</strong> ${today}</p>
          <p><strong>Sales Order Number:</strong> ${salesOrderNumber}</p>

          <table>
            <thead>
              <tr>
                <th>Part Number</th>
                <th>Part Name</th>
                <th>Requested Qty</th>
                <th>Approved Qty</th>
                <th>Existing Qty</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(i => `
                <tr>
                  <td>${i.partNumber}</td>
                  <td>${i.partName}</td>
                  <td>${i.requestedQty}</td>
                  <td>${i.approvedQty}</td>
                  <td>${i.existingQty}</td>
                </tr>`).join('')}
            </tbody>
          </table>

          <div class="footer">
            <div>Prepared By<br/>_____________<br/>Date</div>
            <div>Checked By<br/>_____________<br/>Date</div>
            <div>Approved By<br/>_____________<br/>Date</div>
          </div>

          <div class="barcode">
            <svg id="barcode"></svg>
          </div>

          <script>
            JsBarcode("#barcode", "${salesOrderNumber}", {
              format: "CODE128",
              displayValue: true,
              width: 2,
              height: 50
            });
          </script>
        </body>
      </html>
    `;
    const newWin = window.open('', '_blank');
    newWin.document.write(html);
    newWin.document.close();
    newWin.focus();
    newWin.print();
  };

  const columns = [
   
    { title: 'Requester Name', dataIndex: ['destinationWarehouse', 'name'], key: 'destinationWarehouse' },
   {
  title: 'Request Date',
  dataIndex: 'createdAt',
  key: 'createdAt',
  render: (value) => new Date(value).toLocaleDateString()
},

     { 
      title: 'Order Number', 
      dataIndex: 'salesOrderNumber', 
      key: 'salesOrderNumber',
      render: (text) => (
        <Space>
          {text}
          <Tooltip title="Copy order number">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopyOrderNumber(text)} />
          </Tooltip>
        </Space>
      )
    },
    { title: 'Purpose', dataIndex: 'purpose', key: 'purpose' },
 
        {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<SwapOutlined />} onClick={() => openModal(record)}>
            Transfer
          </Button>
          <Popconfirm
            title="Are you sure to delete this request?"
            onConfirm={() => handleDeleteRequest(record.salesOrderNumber)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const modalColumns = [
    { title: 'ID', dataIndex: 'importerId', key: 'importerId' },
     { title: 'Location', dataIndex: 'sku', key: 'sku' },
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'Requested Qty', dataIndex: 'requestedQty', key: 'requestedQty' },
    { title: 'Existing Qty', dataIndex: 'existingQty', key: 'existingQty' },
    {
      title: 'Approved Qty',
      key: 'approvedQty',
      render: (_, record, index) => (
       <InputNumber
  min={1}
  max={record.existingQty} // use existingQty as limit
  value={record.approvedQty}
  onChange={(val) => handleQtyChange(index, val)}
/>

      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record, index) => (
        <Button danger size="small" onClick={() => handleRemoveItem(index)}>
          Remove
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Title level={4}>Transfer Spare Parts</Title>

      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by Sales Order Number"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={filteredRequests}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={`Transfer Items for ${selectedRequest?.salesOrderNumber || ''}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleTransferSubmit}
        width={850}
        okText="Confirm Transfer"
      >
        <Table
          columns={modalColumns}
          dataSource={items}
          rowKey={(record) => record.sparepartId}
          pagination={false}
        />
      </Modal>
    </div>
  );
};

export default TransferSpareParts;
